import { useLayoutEffect, useRef, useState } from 'react'
import { cn } from '@/lib/utils'

export interface BarsProps {
  /** One label per bucket (a day "2026-10-17" or an hour), oldest first. */
  labels: string[]
  /** The wider measure, drawn as the full column (sessions, paywall shown). */
  outer: number[]
  /** The subset, drawn inside it from the baseline (users, purchases). */
  inner?: number[]
  outerFill?: string
  innerFill?: string
  /** The hover text of bucket i. */
  title: (i: number) => string
  /** How an axis tick reads a label. Default: as is. */
  tick?: (label: string) => string
  height?: number
  /** Makes the columns pickable (a day → its hours); `selected` is lit. */
  onPick?: (i: number) => void
  selected?: number | null
  /** The chart's name, read out. */
  label: string
  className?: string
}

function pct(value: number, max: number): string {
  if (max <= 0 || value <= 0) return '0%'
  return `${Math.max(2, (value / max) * 100)}%`
}

const TICK_CHAR_PX = 6.5
const TICK_GAP_PX = 12
/** The gap between columns: 2px, 1px once a window has more than 45 buckets
 *  (90 days) so the columns still fit a phone. */
const gapPx = (count: number) => (count > 45 ? 1 : 2)
/** The widest a drawn bar gets. The column (the click and tick slot) is not
 *  capped: it fills the chart whatever the bucket count, and a bar sits
 *  centred in it. */
const BAR_MAX_PX = 56

/** Which buckets get an axis label.
 *
 *  Counted back from the last bucket in steps, then the first is added. The
 *  step is the larger of the old rule (every bucket up to 14, weekly to 45,
 *  then fortnightly) and the room one label needs: with the chart's measured
 *  `width` a label ("09. 18") takes some columns and the end labels, which
 *  are anchored to the edge instead of centred, take half a label more. A
 *  regular tick too close to the first label is dropped, so two labels are
 *  never drawn over each other ("09.18" over "09.19" read "099.189" in the
 *  drawer). Without a width (a first render, a test) the old rule stands. */
export function tickIndexes(count: number, labelChars: number, width: number): Set<number> {
  if (count <= 0) return new Set()
  const base = count <= 14 ? 1 : count <= 45 ? 7 : 14
  const column = width > 0 ? (width - (count - 1) * gapPx(count)) / count + gapPx(count) : 0
  const label = labelChars * TICK_CHAR_PX + TICK_GAP_PX
  // Room in columns: between two centred labels, and between an edge label
  // and its neighbour (half a label more).
  const between = column > 0 ? Math.ceil(label / column) : 1
  const edge = column > 0 ? Math.ceil((label * 1.5) / column) : 1
  const step = Math.max(base, between)
  const last = count - 1
  const ticks = [last]
  for (let i = last - Math.max(step, edge); i > 0; i -= step) ticks.push(i)
  // The first label is left-anchored: a tick closer than `lead` columns would
  // run into it.
  const lead = Math.max(edge, Math.ceil(step * 0.75))
  const kept = ticks.filter((i) => i === last || i >= lead)
  if (last === 0 || last >= edge) kept.push(0)
  return new Set(kept)
}

/** The element's width, kept current. 0 until measured (and in jsdom). */
function useWidth<T extends HTMLElement>() {
  const ref = useRef<T>(null)
  const [width, setWidth] = useState(0)
  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return
    const measure = () => setWidth(el.clientWidth)
    measure()
    if (typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    return () => ro.disconnect()
  }, [])
  return [ref, width] as const
}

/** Columns per bucket, one axis. `inner` is always ≤ `outer` in the pairs
 *  used (sessions ≥ users, shown ≥ bought), so nesting reads "of which".
 *  The columns share the full width; the subset is drawn at half the bar's
 *  width so neither series hides the other.
 *  Ticks thin out as the window grows (see `tickIndexes`): the first and last
 *  show whenever there is room for both. */
export function Bars({
  labels,
  outer,
  inner,
  outerFill = 'bg-chart-1',
  innerFill = 'bg-chart-2',
  title,
  tick = (l) => l,
  height = 128,
  onPick,
  selected = null,
  label,
  className,
}: BarsProps) {
  const max = Math.max(1, ...outer, ...(inner ?? []))
  const [axis, width] = useWidth<HTMLDivElement>()
  const last = labels.length - 1
  const ticks = tickIndexes(labels.length, Math.max(0, ...labels.map((l) => tick(l).length)), width)
  return (
    <figure aria-label={label} className={cn('min-w-0', className)}>
      <div className="flex items-end border-b border-chart-grid" style={{ height, gap: gapPx(labels.length) }}>
        {labels.map((l, i) => {
          const body = (
            <span className="relative mx-auto flex h-full w-full items-end" style={{ maxWidth: BAR_MAX_PX }}>
              <span data-series="outer" className={cn('w-full rounded-t-[3px]', outerFill)} style={{ height: pct(outer[i] ?? 0, max) }} />
              {inner && (
                // Half the width and centred, so the wider measure shows on
                // both sides even when the subset is nearly as tall.
                <span
                  data-series="inner"
                  className={cn('absolute bottom-0 left-1/4 w-1/2 rounded-t-[3px]', innerFill)}
                  style={{ height: pct(inner[i] ?? 0, max) }}
                />
              )}
            </span>
          )
          const cls = cn(
            'relative flex h-full min-w-0 flex-1 items-end rounded-t-[3px]',
            onPick && 'cursor-pointer',
            selected === i ? 'bg-accent/15 ring-1 ring-accent' : 'hover:bg-muted',
          )
          return onPick ? (
            <button key={l} type="button" title={title(i)} aria-label={title(i)} aria-pressed={selected === i} onClick={() => onPick(i)} className={cls}>
              {body}
            </button>
          ) : (
            <span key={l} title={title(i)} className={cls}>
              {body}
            </span>
          )
        })}
      </div>
      <div ref={axis} aria-hidden className="relative mt-1.5 flex h-4 text-micro text-muted-foreground" style={{ gap: gapPx(labels.length) }}>
        {labels.map((l, i) => {
          const show = ticks.has(i)
          const anchor = i === 0 ? 'left-0' : i === last ? 'right-0' : 'left-1/2 -translate-x-1/2'
          return (
            <span key={l} className="relative min-w-0 flex-1">
              {show && <span className={cn('absolute whitespace-nowrap tabular-nums', anchor)}>{tick(l)}</span>}
            </span>
          )
        })}
      </div>
      <figcaption className="mt-1 text-right text-micro tabular-nums text-muted-foreground">최대 {max.toLocaleString('ko-KR')}</figcaption>
    </figure>
  )
}
